import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AppDispatch } from '@/store';
import { HttpProps } from '@/type/pages';
import { v4 as uuidv4 } from 'uuid';
import { error, info } from '@/utils/safetyLog';

const APP_BASE_URL = process.env.NEXT_PUBLIC_APP_BASE_URL;
const instance: AxiosInstance = axios.create({
  baseURL: APP_BASE_URL,
  timeout: 30000,
});

let language: string = 'zh-hk';
let dispatch: AppDispatch | null = null;

export const initAxiosInstance = (appDispatch: AppDispatch, reqLanguage: string): void => {
  dispatch = appDispatch;
  language = reqLanguage;
};

// Request interceptor
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const uuid = uuidv4().replace(/-/g, '');
    const startTime = new Date(); // Ensure headers is an AxiosHeaders instance

    if (!config.headers || !(config.headers instanceof AxiosHeaders)) {
      config.headers = new AxiosHeaders(config.headers);
    }

    config.headers.set('X-Frame-Options', 'DENY');
    config.headers.set('uuid', uuid);

    (config as any).startTime = startTime;
    config.params = {
      traceId: uuid,
      ...config.params,
    };

    config.data = {
      language,
      ...config.data,
    };

    return config;
  },
  (err: AxiosError) => {
    error('Request error:', err);
    return Promise.reject(new Error('Request failed'));
  },
);

// Response interceptor with retry
instance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    const endTime = new Date();
    const startTime = (response.config as any).startTime;
    const duration = startTime ? endTime.getTime() - startTime.getTime() : null;
    const uuid = response.config.headers?.uuid;

    // info(`Response UUID: ${uuid}, Duration: ${duration}ms`);
    return response;
  },
  async (err: AxiosError) => {
    const originalRequest = err.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Retry once on network error or 5xx
    if (!originalRequest._retry && (!err.response || err.response.status >= 500)) {
      originalRequest._retry = true;
      info('Retrying request due to error:', err.message);
      return instance(originalRequest);
    }

    if (err?.config) {
      error('Request failed:', {
        url: err?.config?.url,
        method: err?.config?.method,
        headers: err?.config?.headers,
        data: err?.config?.data,
      });
    }

    if (err?.response) {
      error('Response error:', {
        status: err?.response?.status,
        data: err?.response?.data,
        headers: err?.response?.headers,
      });
    } else {
      error('Network or setup error:', err);
    }

    if (dispatch) {
      dispatch({ type: 'ui/showErrorPopup', payload: err.message });
    }
    return Promise.reject(new Error('Response failed'));
  },
);

// HTTP wrapper function
const http = ({ isLoading, isPop }: HttpProps): AxiosInstance => {
  if (dispatch && isLoading) {
    dispatch({ type: 'ui/showLoading' });
  }
  if (dispatch && isPop) {
    dispatch({ type: 'ui/showPopup', payload: 'Processing request...' });
  }
  return instance;
};

export default http;
